import { useState } from 'react';
import { format, isSameDay } from 'date-fns';
import {
    ArrowLeft,
    Ban,
    Loader2,
    Phone,
    PhoneIncoming,
    PhoneMissed,
    PhoneOutgoing,
    Trash2,
    UserPlus,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { dfLocale } from '@/lib/date-fns-locale';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';

import {
    useBlockedNumberByPhone,
    useBlockNumber,
    useUnblockNumber,
} from '../../hooks/use-blocked-numbers';
import { useCounterpartCallHistory } from '../../hooks/use-call-history';
import { useContactByPhone } from '../../hooks/use-contact-by-phone';
import { useDeleteSavedContact, useSaveNumberAsContact } from '../../hooks/use-call-mutations';
import { useVoiceStore, type IHistoryDetailTarget } from '../../state/voice-ui.store';
import type { ICall, IVoiceContactSnapshot } from '../../types';
import { formatCallDuration } from '../../utils/call-status-meta';
import { CallerIdentity } from './caller-identity';
import { HistoryRecordingPlayer } from './history-recording-player';
import { SaveContactForm } from './save-contact-form';

/** How close to the bottom (px) before the next page loads. */
const LOAD_MORE_THRESHOLD_PX = 80;

/** Which inline flow currently replaces the footer's two-button bar. */
type FooterAction = 'idle' | 'save' | 'confirmDelete' | 'confirmBlock' | 'confirmUnblock';

interface HistoryDetailPanelProps {
    readonly target: IHistoryDetailTarget;
    readonly onBack: () => void;
    /** Funnels into the widget's handleDial (re-entry + minutes guards live there). */
    readonly onCall: (
        toNumber: string,
        options?: { contactId?: string; contact?: IVoiceContactSnapshot },
    ) => void;
    /** True when the widget has an explicit (resized) size — the timeline then
     *  fills the stretched body instead of capping at the design max-height. */
    readonly fill?: boolean;
}

function isMissed(call: Pick<ICall, 'direction' | 'answeredAt'>): boolean {
    return call.direction === 'INBOUND' && call.answeredAt === null;
}

function directionIcon(call: Pick<ICall, 'direction' | 'answeredAt'>) {
    if (call.direction === 'OUTBOUND') {
        return <PhoneOutgoing className="size-3.5 shrink-0 text-text-tertiary" />;
    }
    if (call.answeredAt === null) {
        return <PhoneMissed className="size-3.5 shrink-0 text-error-500" />;
    }
    return <PhoneIncoming className="size-3.5 shrink-0 text-text-tertiary" />;
}

function callContactName(call: ICall): string | null {
    if (!call.contact) return null;
    return [call.contact.firstName, call.contact.lastName].filter(Boolean).join(' ') || null;
}

/** Consecutive same-calendar-day groups over the (already sorted) flat list. */
function groupByDay(calls: readonly ICall[]): Array<{ day: Date; calls: ICall[] }> {
    const groups: Array<{ day: Date; calls: ICall[] }> = [];
    for (const call of calls) {
        const day = new Date(call.startedAt);
        const current = groups[groups.length - 1];
        if (current && isSameDay(current.day, day)) current.calls.push(call);
        else groups.push({ day, calls: [call] });
    }
    return groups;
}

/**
 * Per-number drill-in opened from a History row — contact header (name when
 * saved, green call button) above a calls-only, date-grouped timeline of
 * every call with this counterpart across all org lines, with inline
 * recording playback. A footer bar offers Save (the shared SaveContactForm)
 * and Block (with an optional reason note), flipping to Delete contact /
 * Unblock when the number is already saved / blocked. Calls placed from here
 * return to this view when they end (the store keeps 'historyDetail' across
 * call transitions).
 */
export function HistoryDetailPanel({
    target,
    onBack,
    onCall,
    fill = false,
}: HistoryDetailPanelProps) {
    const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const canReadCalls = hasAnyPermission([PERMISSION.VOICE_CALLS_READ]);
    const canReadContacts = hasAnyPermission([PERMISSION.CONTACTS_READ]);
    // Unblock is DELETE /voice/blocked-numbers/:id — manage-only server-side,
    // so agents without the permission get a static badge, not a dead button.
    const canManageVoice = hasAnyPermission([PERMISSION.VOICE_CALLS_MANAGE]);

    const activeCall = useVoiceStore((s) => s.activeCall);
    const ringingQueue = useVoiceStore((s) => s.ringingQueue);

    const { data, isLoading, isError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } =
        useCounterpartCallHistory({
            counterpartNumber: target.number,
            enabled: !permissionsLoading && canReadCalls,
        });
    // Saved-ness by exact phone — independent of whether old call rows were
    // ever linked to the contact. Skipped without contacts read (we couldn't
    // tell "unsaved" from "not allowed to know").
    const contactQuery = useContactByPhone(target.number, !permissionsLoading && canReadContacts);
    const blockedQuery = useBlockedNumberByPhone(
        target.number,
        !permissionsLoading && canReadCalls,
    );
    const saveMutation = useSaveNumberAsContact();
    const deleteMutation = useDeleteSavedContact();
    const blockMutation = useBlockNumber();
    const unblockMutation = useUnblockNumber();
    const [footerAction, setFooterAction] = useState<FooterAction>('idle');
    const [blockReason, setBlockReason] = useState('');

    // In-flight calls come back from the API too — the History tab's Current
    // section owns them; keep the timeline to finished calls only.
    const currentCallIds = new Set<string>([
        ...(activeCall ? [activeCall.callId] : []),
        ...ringingQueue.map((ringing) => ringing.callId),
    ]);
    const calls = (data?.pages.flatMap((page) => page.data) ?? []).filter(
        (call) => !currentCallIds.has(call.id),
    );

    const savedContact = contactQuery.data ?? null;
    const savedContactName = savedContact
        ? [savedContact.firstName, savedContact.lastName].filter(Boolean).join(' ') || null
        : null;
    const blockedRow = blockedQuery.data ?? null;
    // Header label precedence: contacts lookup → the opening row's snapshot →
    // the newest call's linked contact → raw number (CallerIdentity fallback).
    const resolvedName =
        savedContactName ??
        target.contact?.name ??
        calls.map(callContactName).find(Boolean) ??
        null;

    const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        const el = event.currentTarget;
        const nearBottom =
            el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX;
        if (nearBottom && hasNextPage && !isFetchingNextPage) void fetchNextPage();
    };

    const handleCall = (): void => {
        onCall(
            target.number,
            savedContact && savedContactName
                ? {
                      contactId: savedContact.id,
                      contact: { id: savedContact.id, name: savedContactName },
                  }
                : undefined,
        );
    };

    const closeFooterAction = (): void => {
        setFooterAction('idle');
        setBlockReason('');
    };

    const handleConfirmBlock = (): void => {
        const reason = blockReason.trim();
        blockMutation.mutate(
            { phone: target.number, ...(reason ? { reason } : {}) },
            { onSuccess: closeFooterAction },
        );
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center gap-1 px-2 pt-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={onBack}
                    aria-label="Back to history"
                >
                    <ArrowLeft className="size-4" />
                </Button>
                <span className="text-xs font-medium text-text-secondary">History</span>
            </div>

            <div className="flex items-center gap-2 px-4 py-2">
                <CallerIdentity
                    name={resolvedName}
                    number={target.number}
                    className="min-w-0 flex-1"
                />
                <Button
                    type="button"
                    size="icon"
                    className="shrink-0 rounded-full bg-success-500 text-white shadow-sm hover:bg-success-600"
                    disabled={Boolean(activeCall)}
                    title={activeCall ? 'Finish the current call first' : undefined}
                    aria-label={`Call ${target.number}`}
                    onClick={handleCall}
                >
                    <Phone className="size-4" />
                </Button>
            </div>

            {!permissionsLoading && !canReadCalls ? (
                <div className="p-6 text-center text-sm text-text-tertiary">
                    You don&apos;t have permission to view call history
                </div>
            ) : isLoading || permissionsLoading ? (
                // The timeline query is disabled until permissions resolve, so
                // `isLoading` is false during that window — key the skeleton off
                // `permissionsLoading` too (same trap as the History tab).
                <div className="flex flex-col gap-2 p-4">
                    {Array.from({ length: 4 }, (_, i) => (
                        <Skeleton key={i} className="h-14 w-full" />
                    ))}
                </div>
            ) : isError ? (
                // A failed fetch must never masquerade as an empty history.
                <div className="flex flex-col items-center gap-2 p-6 text-center">
                    <p className="text-sm text-text-tertiary">Couldn&apos;t load calls.</p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void refetch()}
                    >
                        Retry
                    </Button>
                </div>
            ) : calls.length === 0 ? (
                <div className="p-6 text-center text-sm text-text-tertiary">
                    No calls with this number yet
                </div>
            ) : (
                <div
                    data-slot="voice-history-detail-timeline"
                    className={cn(
                        'flex min-h-0 flex-col gap-2 overflow-y-auto px-3 pt-1 pb-3',
                        fill ? 'flex-1' : 'max-h-80',
                    )}
                    onScroll={handleScroll}
                >
                    {groupByDay(calls).map((group) => (
                        <div key={group.day.toISOString()} className="flex flex-col gap-2">
                            <span className="mx-auto rounded-full bg-black/[0.04] px-2.5 py-0.5 text-[10px] font-medium text-text-tertiary dark:bg-white/[0.06]">
                                {format(group.day, 'MMM d, yyyy', { locale: dfLocale() })}
                            </span>
                            {group.calls.map((call) => {
                                const stored = call.recordings?.find(
                                    (recording) => recording.status === 'STORED',
                                );
                                return (
                                    <div
                                        key={call.id}
                                        className="flex flex-col gap-1.5 rounded-xl border border-white/50 bg-white/40 p-2.5 dark:border-white/10 dark:bg-white/[0.04]"
                                    >
                                        <div className="flex items-center gap-2">
                                            {directionIcon(call)}
                                            <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-primary">
                                                {callContactName(call) ??
                                                    resolvedName ??
                                                    target.number}
                                            </span>
                                            <span className="shrink-0 text-[10px] text-text-tertiary tabular-nums">
                                                {format(new Date(call.startedAt), 'p', {
                                                    locale: dfLocale(),
                                                })}
                                            </span>
                                        </div>
                                        {isMissed(call) ? (
                                            <span className="text-[10px] font-medium text-error-500">
                                                Missed
                                            </span>
                                        ) : (
                                            call.durationSeconds !== null && (
                                                <span className="text-[10px] text-text-tertiary tabular-nums">
                                                    {formatCallDuration(call.durationSeconds)}
                                                </span>
                                            )
                                        )}
                                        {stored && <HistoryRecordingPlayer recording={stored} />}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    {isFetchingNextPage && (
                        <div className="flex justify-center py-2">
                            <Spinner className="size-4 text-text-tertiary" />
                        </div>
                    )}
                </div>
            )}

            {/* Footer actions — Save/Block flipping to Delete contact/Unblock;
                an active flow replaces the bar in place (widget idiom). */}
            <div className="px-4 pt-1 pb-3">
                {footerAction === 'save' ? (
                    <SaveContactForm
                        idPrefix="history-detail"
                        phone={target.number}
                        isSaving={saveMutation.isPending}
                        onCancel={closeFooterAction}
                        onSave={(name) =>
                            saveMutation.mutate(
                                { firstName: name, phone: target.number },
                                { onSuccess: closeFooterAction },
                            )
                        }
                    />
                ) : footerAction === 'confirmDelete' && savedContact ? (
                    <div className="flex flex-col gap-2 rounded-xl border border-error-200 bg-error-50/90 p-2.5 dark:border-error-900 dark:bg-error-950/40">
                        <p className="text-xs text-text-secondary">
                            Delete {savedContactName ?? target.number} from contacts?
                        </p>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                disabled={deleteMutation.isPending}
                                onClick={closeFooterAction}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="flex-1 gap-1.5"
                                disabled={deleteMutation.isPending}
                                onClick={() =>
                                    deleteMutation.mutate(savedContact.id, {
                                        onSuccess: closeFooterAction,
                                    })
                                }
                            >
                                {deleteMutation.isPending ? (
                                    <Loader2 className="size-3.5 animate-spin" />
                                ) : (
                                    <Trash2 className="size-3.5" />
                                )}
                                Delete
                            </Button>
                        </div>
                    </div>
                ) : footerAction === 'confirmBlock' ? (
                    <div className="flex flex-col gap-2 rounded-xl border border-error-200 bg-error-50/90 p-2.5 dark:border-error-900 dark:bg-error-950/40">
                        <p className="text-xs text-text-secondary">
                            Block {resolvedName ?? target.number}? Once blocked, they are no longer
                            able to call or send messages.
                        </p>
                        <Label htmlFor="history-detail-block-reason">Reason (optional)</Label>
                        <Textarea
                            id="history-detail-block-reason"
                            rows={2}
                            placeholder="Why is this number being blocked?"
                            value={blockReason}
                            onChange={(event) => setBlockReason(event.target.value)}
                        />
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                disabled={blockMutation.isPending}
                                onClick={closeFooterAction}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="flex-1 gap-1.5"
                                disabled={blockMutation.isPending}
                                onClick={handleConfirmBlock}
                            >
                                {blockMutation.isPending ? (
                                    <Loader2 className="size-3.5 animate-spin" />
                                ) : (
                                    <Ban className="size-3.5" />
                                )}
                                Block number
                            </Button>
                        </div>
                    </div>
                ) : footerAction === 'confirmUnblock' && blockedRow ? (
                    <div className="flex flex-col gap-2 rounded-xl border border-error-200 bg-error-50/90 p-2.5 dark:border-error-900 dark:bg-error-950/40">
                        <p className="text-xs text-text-secondary">
                            Unblock {target.number}? They will be able to call and send messages
                            again.
                        </p>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                disabled={unblockMutation.isPending}
                                onClick={closeFooterAction}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="flex-1 gap-1.5"
                                disabled={unblockMutation.isPending}
                                onClick={() =>
                                    unblockMutation.mutate(blockedRow.id, {
                                        onSuccess: closeFooterAction,
                                    })
                                }
                            >
                                {unblockMutation.isPending ? (
                                    <Loader2 className="size-3.5 animate-spin" />
                                ) : (
                                    <Ban className="size-3.5" />
                                )}
                                Unblock number
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        {canReadContacts &&
                            contactQuery.isSuccess &&
                            (savedContact ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="gap-1.5 text-error-600 hover:text-error-700"
                                    onClick={() => setFooterAction('confirmDelete')}
                                >
                                    <Trash2 className="size-3.5" />
                                    Delete contact
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    className="gap-1.5"
                                    onClick={() => setFooterAction('save')}
                                >
                                    <UserPlus className="size-3.5" />
                                    Save
                                </Button>
                            ))}
                        {blockedQuery.isSuccess &&
                            (blockedRow ? (
                                canManageVoice ? (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="gap-1.5"
                                        onClick={() => setFooterAction('confirmUnblock')}
                                    >
                                        <Ban className="size-3.5" />
                                        Unblock
                                    </Button>
                                ) : (
                                    <Badge
                                        variant="danger"
                                        className="self-center justify-self-start"
                                    >
                                        Blocked
                                    </Badge>
                                )
                            ) : (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="gap-1.5 text-error-600 hover:text-error-700"
                                    onClick={() => setFooterAction('confirmBlock')}
                                >
                                    <Ban className="size-3.5" />
                                    Block
                                </Button>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
}
